import Api from './api';
import {
  FaShoppingCart,
  FaStore,
  FaBriefcase,
  FaIndustry,
  FaPills,
  FaHospital,
  FaUtensils,
  FaCar,
  FaLaptop,
  FaBook,
  FaHardHat,
  FaFolder
} from 'react-icons/fa';
/**
 * Serviço para operações de projetos
 * Integração com ProjectController do backend
 */
class ProjectService {
  
  /**
   * Buscar todos os projetos com paginação e filtros
   * @param {Object} params - Parâmetros de busca e paginação
   * @returns {Promise} Response com lista paginada de projetos
   */
  static async getAll(params = {}) {
    const requestData = {
      page: params.page || 1,
      pageSize: params.pageSize || 10,
      name: params.search || '',
      identifier: params.identifier || '',
      sorterField: params.sorterField || '',
      sortOrder: params.sortOrder || 'desc'
    };

    return await Api.post('/project/getAll', requestData);
  }

  /**
   * Buscar projeto por ID
   * @param {string} id - ID do projeto
   * @returns {Promise} Dados do projeto
   */
  static async getById(id) {
    return await Api.get(`/project/getById/${id}`);
  }

  /**
   * Criar ou atualizar projeto
   * @param {Object} projectData - Dados do projeto
   * @returns {Promise} Projeto criado/atualizado
   */
  static async upsert(projectData) {
    return await Api.post('/project/upsert', projectData);
  }

  /**
   * Deletar projeto
   * @param {string} id - ID do projeto
   * @returns {Promise} Resultado da operação
   */
  static async delete(id) {
    return await Api.delete(`/project/${id}`);
  }

  /**
   * Upload de logo do projeto
   * @param {string} projectId - ID do projeto
   * @param {File} file - Arquivo de imagem
   * @returns {Promise} URL do arquivo uploadado
   */
  static async uploadLogo(projectId, file) {
    const isFileLike = (
      (typeof File !== 'undefined' && file instanceof File) ||
      (typeof Blob !== 'undefined' && file instanceof Blob)
    );

    if (!file || typeof file === 'string' || !isFileLike) {
      throw new Error('ProjectService.uploadLogo espera um arquivo real, não uma URL/string.');
    }

    const formData = new FormData();
    formData.append('files', file);
    formData.append('idProject', projectId);

    const response = await Api.post('/project/uploadFile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // A resposta vem com a URL completa do Azure Blob Storage
    // Exemplo: { response: "https://data4company.blob.core.windows.net/...", statusCode: 200 }
    
    return {
      ...response,
      data: response.data.response // Extrair a URL da propriedade response
    };
  }

  /**
   * Upload de planilha de orçamento
   * @param {string} projectId - ID do projeto
   * @param {File} file - Arquivo da planilha
   * @returns {Promise} URL do arquivo uploadado
   */
  static async uploadOrcamento(projectId, file) {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('idProject', projectId);

    const response = await Api.post('/project/uploadFileOrcamento', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // A resposta vem com a URL completa do Azure Blob Storage
    
    return {
      ...response,
      data: response.data.response // Extrair a URL da propriedade response
    };
  }

  /**
   * Remover logo do projeto
   * @param {string} projectId - ID do projeto
   * @returns {Promise} Resultado da operação
   */
  static async removeLogo(projectId) {
    return await Api.post('/project/removeFile', {
      projectId: projectId
    });
  }

  /**
   * Remover planilha de orçamento
   * @param {string} projectId - ID do projeto
   * @returns {Promise} Resultado da operação
   */
  static async removeOrcamento(projectId) {
    return await Api.post('/project/removeFileOrcamento', {
      projectId: projectId
    });
  }

  /**
   * Obter arquivo do projeto
   * @param {string} fileName - Nome do arquivo
   * @returns {Promise} Stream do arquivo
   */
  static async getFile(fileName) {
    return await Api.get(`/project/getFile/${fileName}`, {
      responseType: 'blob'
    });
  }

  /**
   * Mapear dados do frontend para backend
   * @param {Object} frontendData - Dados do formulário frontend
   * @returns {Object} Dados formatados para o backend
   */
  static mapToBackend(frontendData) {
    
    const backendData = {
      id: frontendData.id || '00000000-0000-0000-0000-000000000000',
      name: frontendData.name || '',
      identifier: frontendData.name?.replace(/\s+/g, '_').toLowerCase() || '',
      description: frontendData.description || '',
      active: frontendData.active !== undefined ? frontendData.active : true,
      integrationType: frontendData.integrationType || '',
      apiKey: frontendData.apiKey || '',
      endpointUrl: frontendData.endpointUrl || '',
      type: frontendData.type || '',
      status: frontendData.statusCode !== undefined ? parseInt(frontendData.statusCode) : 1,
      cnpj: frontendData.cnpj || '',
      email: frontendData.email || '',
      phone: frontendData.phone || ''
    };
    
    return backendData;
  }

  /**
   * Mapear dados do backend para frontend
   * @param {Object} backendData - Dados vindos do backend
   * @returns {Object} Dados formatados para o frontend
   */
  static mapToFrontend(backendData) {
    const syncsList = backendData.syncs || backendData.synchronizations || [];
    return {
      id: backendData.id,
      name: backendData.name,
      description: backendData.description,
      icon: this.getProjectIcon(backendData.type),
      iconBackground: this.getProjectIconBackground(backendData.type),
      status: backendData.active ? 'active' : 'inactive',
      connectedErps: Array.isArray(syncsList) ? syncsList.length : 0,
      syncTime: this.formatSyncTime(backendData.updated),
      tags: this.getProjectTags(backendData),
      syncs: syncsList,
      files: backendData.files,
      logoUrl: backendData.logoUrl,
      fileOrcamento: backendData.fileOrcamento,
      type: backendData.type,
      integrationType: backendData.integrationType,
      apiKey: backendData.apiKey,
      endpointUrl: backendData.endpointUrl,
      active: backendData.active,
      statusCode: backendData.status,
      // Campos diretos no projeto
      cnpj: backendData.cnpj || '',
      email: backendData.email || '',
      phone: backendData.phone || '',
      companyInfo: {
        name: backendData.name,
        description: backendData.description,
        cnpj: backendData.cnpj || '',
        email: backendData.email || '',
        phone: backendData.phone || '',
        type: backendData.type || ''
      }
    };
  }

  /**
   * Obter ícone do projeto baseado no tipo
   * @param {string} type - Tipo do projeto
   * @returns {string} Emoji do ícone
   */
  static getProjectIcon = (type) => {
    if (!type) return <FaFolder />;
  
    const normalizedType = type.toLowerCase();
  
    const typeIcons = {
      'ecommerce': <FaShoppingCart />,
      'e-commerce': <FaShoppingCart />,
      'loja': <FaShoppingCart />,
      'varejo': <FaStore />,
      'servicos': <FaBriefcase />,
      'serviços': <FaBriefcase />,
      'consultoria': <FaBriefcase />,
      'industria': <FaIndustry />,
      'indústria': <FaIndustry />,
      'metalurgica': <FaIndustry />,
      'metalúrgica': <FaIndustry />,
      'farmacia': <FaPills />,
      'farmácia': <FaPills />,
      'saude': <FaHospital />,
      'saúde': <FaHospital />,
      'alimentacao': <FaUtensils />,
      'alimentação': <FaUtensils />,
      'restaurante': <FaUtensils />,
      'automovel': <FaCar />,
      'automóvel': <FaCar />,
      'autopecas': <FaCar />,
      'autopeças': <FaCar />,
      'tecnologia': <FaLaptop />,
      'software': <FaLaptop />,
      'educacao': <FaBook />,
      'educação': <FaBook />,
      'construcao': <FaHardHat />,
      'construção': <FaHardHat />
    };
  
    return typeIcons[normalizedType] || <FaFolder />;
  };
  /**
   * Obter cor de fundo do ícone baseado no tipo
   * @param {string} type - Tipo do projeto
   * @returns {string} Cor de fundo
   */
  static getProjectIconBackground(type) {
    if (!type) return '#722ed1';
    
    const typeColors = {
      'ecommerce': 'rgba(59, 130, 246, 0.1)',
      'e-commerce': 'rgba(59, 130, 246, 0.1)',
      'loja': 'rgba(59, 130, 246, 0.1)',
      'varejo': 'rgba(34, 197, 94, 0.1)',
      'servicos': 'rgba(139, 92, 246, 0.1)',
      'serviços': 'rgba(139, 92, 246, 0.1)',
      'consultoria': 'rgba(139, 92, 246, 0.1)',
      'industria': 'rgba(107, 114, 128, 0.1)',
      'indústria': 'rgba(107, 114, 128, 0.1)',
      'metalurgica': 'rgba(107, 114, 128, 0.1)',
      'metalúrgica': 'rgba(107, 114, 128, 0.1)',
      'farmacia': 'rgba(34, 197, 94, 0.1)',
      'farmácia': 'rgba(34, 197, 94, 0.1)',
      'saude': 'rgba(244, 63, 94, 0.1)',
      'saúde': 'rgba(244, 63, 94, 0.1)',
      'alimentacao': 'rgba(251, 146, 60, 0.1)',
      'alimentação': 'rgba(251, 146, 60, 0.1)',
      'restaurante': 'rgba(251, 146, 60, 0.1)',
      'automovel': 'rgba(245, 158, 11, 0.1)',
      'automóvel': 'rgba(245, 158, 11, 0.1)',
      'autopecas': 'rgba(245, 158, 11, 0.1)',
      'autopeças': 'rgba(245, 158, 11, 0.1)',
      'tecnologia': 'rgba(99, 102, 241, 0.1)',
      'software': 'rgba(99, 102, 241, 0.1)',
      'educacao': 'rgba(16, 185, 129, 0.1)',
      'educação': 'rgba(16, 185, 129, 0.1)',
      'construcao': 'rgba(168, 85, 247, 0.1)',
      'construção': 'rgba(168, 85, 247, 0.1)'
    };

    const normalizedType = type.toLowerCase().trim();
    return typeColors[normalizedType] || '#722ed1';
  }

  /**
   * Obter tags do projeto baseado no status
   * @param {Object} project - Dados do projeto
   * @returns {Array} Array de tags
   */
  static getProjectTags(project) {
    const tags = [];
    
    if (project.active) {
      tags.push('Ativo');
    } else {
      tags.push('Inativo');
    }

    // Status baseado no código numérico
    switch (project.status) {
      case 0:
        tags.push('Configurando');
        break;
      case 1:
        tags.push('Operacional');
        break;
      case 2:
        tags.push('Manutenção');
        break;
      case 3:
        tags.push('Suspenso');
        break;
    }

    return tags;
  }

  /**
   * Extrair tipos únicos de uma lista de projetos
   * @param {Array} projects - Lista de projetos
   * @returns {Array} Array de tipos únicos
   */
  static extractUniqueTypes(projects) {
    const types = projects
      .map(p => p.type)
      .filter(type => type && type.trim())
      .map(type => type.trim());
    
    return [...new Set(types)].sort();
  }

  /**
   * Formatar tempo de sincronização
   * @param {string} dateString - Data em string
   * @returns {string} Tempo formatado
   */
  static formatSyncTime(dateString) {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes}min atrás`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'} atrás`;
  }

  /**
   * Buscar projetos simplificados para selects
   * @returns {Promise} Lista de projetos
   */
  static async getProjects() {
    try {
      const response = await this.getAll({ page: 1, pageSize: 100 });
      const projects = response.data.response.data || [];
      
      return projects.map(project => ({
        id: project.id,
        name: project.name,
        type: project.type,
        active: project.active
      }));
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      return [];
    }
  }
}

export default ProjectService;